import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Activity, Loader2, RotateCcw, TriangleAlert } from 'lucide-react'
import { apiPost } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { cn } from '@/lib/cn'
import { Derived } from './controls'
import {
  HUNT_SETTINGS_KEY,
  type HuntLive,
  type HuntRow,
  type IspPoolStats,
  type PoolStats,
} from './fields'

/**
 * The parts of this page that are not settings: the figures the settings add up
 * to, the one tool that acts immediately, and the list of what is running.
 *
 * They live together because they share a property none of the fields have —
 * everything here is READ from the system rather than typed into it, and none of
 * it is committed by Update.
 */

/* ------------------------------------------------------------------ *
 * Derived figures
 * ------------------------------------------------------------------ */

/**
 * What the schedule watch is actually achieving.
 *
 * The number that matters is MEASURED, not configured. Workers loop on
 * completion with no gap, so the rate is set by the round trip rather than by a
 * setting — it cannot be derived from the form at all, only observed from the
 * live hunts. A computed figure here would be a confident number describing
 * nothing.
 */
export function WatchRateLine(p: { hunts: HuntRow[]; workers: number; gapMs: number }) {
  const observedRate = p.hunts.reduce((n, h) => n + (h.rate ?? 0), 0)
  const observedRtt =
    p.hunts.length > 0
      ? Math.round(p.hunts.reduce((n, h) => n + (h.meanRttMs ?? 0), 0) / p.hunts.length)
      : 0

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-accent/25 bg-accent/5 px-3 py-2.5">
      <Activity aria-hidden="true" className="size-4 shrink-0 text-accent" />
      {observedRate > 0 ? (
        <>
          <span className="text-[13px] text-muted">Checking</span>
          <span className="tabular text-[15px] font-medium text-accent">{observedRate}/sec</span>
          <span className="text-[12px] text-muted">
            across {p.hunts.length} hunt{p.hunts.length === 1 ? '' : 's'}
            {observedRtt > 0 ? ` · ~${observedRtt}ms round trip` : ''}
          </span>
        </>
      ) : (
        /*
          The sentence has to follow the gap field, and once did not: it read
          "re-checking the instant its answer lands" unconditionally — true at
          the default of 0 and false the moment anyone sets a gap, which is
          exactly when somebody would be reading this line to check what they
          had just done.
        */
        <span className="text-[13px] text-muted">
          {p.workers} worker{p.workers === 1 ? '' : 's'} per customer, each on its own ISP,{' '}
          {p.gapMs > 0
            ? `waiting at least ${p.gapMs}ms between its own checks.`
            : 're-checking the instant its answer lands.'}{' '}
          <span className="text-muted/80">The real rate appears here once a hunt is running.</span>
        </span>
      )}
    </div>
  )
}

/**
 * The account budget, stated in CUSTOMERS rather than rows.
 *
 * `workers × accounts` are claimed for a hunt's whole life out of a pool shared
 * with another system, so the number that matters is not "how many accounts
 * exist" but "how many customers can run at once before the next one falls back
 * to its own token". Reporting it that way turns an invisible ceiling into an
 * arithmetic an operator can act on.
 */
export function PoolBudgetLine(p: {
  pool: PoolStats
  watchWorkers: number
  watchAccounts: number
  gaWorkers: number
  gaAccounts: number
}) {
  /*
   * The schedule fleet and the GA fleet are added, not maxed, and that is the
   * honest worst case.
   *
   * On the Current flow the GA workers arm BESIDE the watch while the assigned
   * shift has no UUID, so one customer can hold both halves of this sum at
   * once — for the minutes it takes a listing to name the shift. The number an
   * operator needs before raising a dial is the worst case for the pool rather
   * than the typical row, and this is it.
   */
  const perCustomer = p.watchWorkers * p.watchAccounts + p.gaWorkers * p.gaAccounts
  /*
   * The ceiling that is actually reached first.
   *
   * `free` counts rows nobody holds right now, so this already accounts for
   * whatever the sibling system has taken — which is the honest version of the
   * question, since the pool is not ours alone.
   */
  const concurrent = perCustomer > 0 ? Math.floor(p.pool.free / perCustomer) : Infinity

  if (perCustomer === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-line bg-s2/50 px-3 py-2 text-[12.5px]">
      <span className="text-muted">
        {/*
          "on the default", because it is no longer true of everybody. The
          Approved list can give one customer its own worker counts, and those
          rows consume this pool at their own rate — so the figure describes the
          customers using the settings above, which is most of them and not all.
        */}
        <strong className="tabular text-fg">{perCustomer}</strong> accounts per customer on the
        default
        {p.gaAccounts > 0 ? (
          <span className="text-muted/80">
            {' '}
            ({p.watchWorkers}×{p.watchAccounts} watch + {p.gaWorkers}×{p.gaAccounts} GA)
          </span>
        ) : null}
      </span>
      <span aria-hidden="true" className="text-muted/60">
        ·
      </span>
      <span className="text-muted">
        pool has <strong className="tabular text-fg">{p.pool.ready ? p.pool.free : '—'}</strong> free
        {p.pool.held > 0 ? `, ${p.pool.held} held by us` : ''}
      </span>
      {p.pool.ready ? (
        <span className={cn('tabular ml-auto', concurrent >= 5 ? 'text-muted' : 'text-warn')}>
          ≈ {concurrent} customer{concurrent === 1 ? '' : 's'} at once
        </span>
      ) : null}
    </div>
  )
}

/**
 * The window the worked example below is priced against.
 *
 * A schedule that reports ACTIVE while refusing every claim is the shape both
 * settings in that card exist to bound, and forty seconds is a long but entirely
 * ordinary one — long enough that the difference between the two settings is
 * visible, short enough that nobody dismisses it as a pathological case.
 */
const CLAIM_EXAMPLE_MS = 40_000

/**
 * What the claim cap and the claim gap cost TOGETHER.
 *
 * Each field's own text can only describe that field. The thing an operator is
 * actually deciding is what the PAIR does, and read separately each one looks
 * like it could be the whole protection while neither is: the cap alone still
 * permits every read in the seconds before it trips, and the gap alone spaces
 * calls forever without ever stopping. Naming which of the two is binding turns
 * "are these numbers sensible" into a question with a visible answer.
 *
 * Reads the DRAFT rather than the saved settings, so it moves while the numbers
 * are being typed — before the decision is committed, which is the only moment
 * it can change one.
 */
export function ClaimCostLine({ cap, gapMs }: { cap: number; gapMs: number }) {
  /*
   * Both fields are free text mid-edit, so an empty box is `Number('') === 0`
   * and a half-typed minus is `NaN`. Treated as "off" rather than guarded
   * against: 0 IS off for both settings, so the sentence stays true while
   * somebody is clearing a field rather than flickering through a wrong claim.
   */
  const safeCap = Number.isFinite(cap) && cap > 0 ? Math.floor(cap) : 0
  const safeGap = Number.isFinite(gapMs) && gapMs > 0 ? Math.floor(gapMs) : 0
  const seconds = Math.round(CLAIM_EXAMPLE_MS / 1000)

  // First claim instant, then one per gap — so the gap allows N+1, not N.
  const byGap = safeGap === 0 ? Infinity : 1 + Math.floor(CLAIM_EXAMPLE_MS / safeGap)
  const byCap = safeCap === 0 ? Infinity : safeCap
  const calls = Math.min(byGap, byCap)

  const binding =
    byCap === Infinity && byGap === Infinity
      ? 'nothing bounds this — one claim per read, per worker'
      : byCap <= byGap
        ? 'the cap is what stops it, not the gap'
        : 'the gap is what holds it down, and the cap is never reached'

  return (
    <Derived>
      <span className="text-fg">Worked example.</span> A shift that stays open {seconds}s and
      refuses every claim (HTTP 429, 403 or 502) costs{' '}
      {calls === Infinity ? (
        <span className="text-warn">a claim for every read</span>
      ) : (
        <strong className="tabular text-fg">
          about {calls} claim{calls === 1 ? '' : 's'}
        </strong>
      )}{' '}
      · {binding}
    </Derived>
  )
}

/**
 * What one burst costs, in calls and in wall-clock time.
 *
 * Both figures are easy to get badly wrong from the boxes alone. "200 passes"
 * reads as a modest number until it is multiplied by three calls and lands at
 * six hundred requests from a single pinned address; and the duration is
 * invisible entirely, because it depends on a round trip nobody typed. Stating
 * them is what stops a burst being configured to outlive the opening it is for.
 *
 * Deliberately approximate. The point is the order of magnitude.
 */
export function BurstCostLine(p: {
  waitSec: number
  loops: number
  creates: number
  confirms: number
  gapMs: number
}) {
  /*
   * Every field is free text mid-edit, so an empty box is `Number('') === 0`
   * and a half-typed minus is `NaN`. Both collapse to 0 rather than being
   * guarded against, so the sentence stays sane while somebody clears a field.
   */
  const n = (v: number): number => (Number.isFinite(v) && v > 0 ? Math.floor(v) : 0)
  const perLoop = n(p.creates) + n(p.confirms)
  const iterations = n(p.loops)
  const calls = perLoop * iterations

  /*
   * An assumed round trip, because there is no measured one on this page.
   *
   * 400ms is around what these endpoints answer in through a residential proxy.
   * It is used only to turn a call count into a duration an operator can compare
   * against the length of an opening, which is the comparison that matters.
   */
  const ROUND_TRIP_MS = 400
  const runMs = iterations * (perLoop * ROUND_TRIP_MS + n(p.gapMs))
  const totalSec = Math.round(n(p.waitSec) + runMs / 1000)

  return (
    <Derived>
      {perLoop === 0 ? (
        <span className="text-warn">
          No creates and no confirms — a pass that sends nothing. One confirm will be forced on
          save.
        </span>
      ) : (
        <>
          <span className="text-fg">One burst.</span> Up to{' '}
          <strong className="tabular text-fg">{calls} calls</strong> ({iterations} × {perLoop}),
          running about{' '}
          <strong className={cn('tabular', totalSec > 600 ? 'text-warn' : 'text-fg')}>
            {totalSec < 120 ? `${totalSec}s` : `${Math.round(totalSec / 60)} min`}
          </strong>{' '}
          including the wait · all of it from this customer&rsquo;s one pinned address
        </>
      )}
    </Derived>
  )
}

/**
 * How fast a run hunting on the job alone confirms, read off the gap range.
 *
 * The figure the no-UUID card exists to make visible: at 100–200ms one run is
 * five to ten calls a second, all from one pinned address, for the length of an
 * opening — which is the number to look at before lowering either end.
 */
export function NoUuidRateLine(p: { minMs: number; maxMs: number }) {
  // Free text mid-edit, collapsed to 0 exactly as `BurstCostLine` does.
  const n = (v: number): number => (Number.isFinite(v) && v > 0 ? Math.floor(v) : 0)
  const lo = n(p.minMs)
  // The server raises a maximum typed below the minimum, so read it that way too.
  const hi = Math.max(lo, n(p.maxMs))
  const rate = (ms: number): string => (1000 / ms).toFixed(1)

  return (
    <Derived>
      {hi === 0 ? (
        <span className="text-warn">
          Back-to-back. Each call is still awaited, so the rate becomes whatever the endpoint
          answers at — typically several times faster than any figure you would set here.
        </span>
      ) : (
        <>
          About{' '}
          <strong className="tabular text-fg">
            {lo === hi ? rate(hi) : lo === 0 ? `${rate(hi)} or more` : `${rate(hi)}–${rate(lo)}`}
          </strong>{' '}
          confirms a second per customer, every one of them from that customer&rsquo;s single
          pinned address, for as long as the shift stays open.
        </>
      )}
    </Derived>
  )
}

/**
 * The cross-field rule the server applies to the session pair, said before it
 * is applied rather than discovered afterwards.
 *
 * A renewal age at or above the session lifetime is a session that dies before
 * anything tries to renew it, and both halves look perfectly reasonable on
 * their own — so nothing on screen said so, and the failure appeared an hour
 * later as a 401 on whatever call happened to be next, possibly the claim. The
 * server silently lowers the renewal to keep ten minutes of runway; this is the
 * only place that is visible.
 */
export function RenewHeadroomNote(p: { lifetimeMin: number; renewMin: number }) {
  const HEADROOM_MIN = 10
  if (!Number.isFinite(p.lifetimeMin) || !Number.isFinite(p.renewMin)) return null
  if (p.renewMin === 0) return null
  const ceiling = Math.max(1, p.lifetimeMin - HEADROOM_MIN)
  if (p.renewMin <= ceiling) return null

  return (
    <Derived>
      A renewal needs runway: the server keeps it at least{' '}
      <strong className="text-fg">{HEADROOM_MIN} minutes</strong> below the lifetime above, so{' '}
      <strong className="tabular text-fg">{p.renewMin}</strong> will be stored as{' '}
      <strong className="tabular text-fg">{ceiling}</strong>. Raise the lifetime to renew later
      than that.
    </Derived>
  )
}

/* ------------------------------------------------------------------ *
 * The pool tool
 * ------------------------------------------------------------------ */

/** What the account release-all endpoint reports back. */
interface PoolReleaseResult {
  ours: number
  /** Rows the OTHER system was holding. The number worth reading. */
  others: number
  rested: number
  freed: number
  total: number
  huntsAffected: number
  leasesDropped: number
}

/** What the ISP release-all endpoint reports back. */
interface IspReleaseResult {
  ours: number
  others: number
  /** Rows that were benched on a WAF cooldown. The ISP pool's own third state. */
  cooling: number
  freed: number
  total: number
}

/**
 * Free every row in a shared pool.
 *
 * A tool, not a setting, so it is its own mutation against its own endpoint
 * rather than a field on the settings form — an ordinary save must never be able
 * to empty a pool.
 *
 * ── Why these need a confirmation and the rest of the page does not ──
 * Every other control here changes what the NEXT hunt does. These write to a
 * collection shared with another running system, immediately, and can free rows
 * that system believes it owns. That is a legitimate thing for an operator to
 * want — it is how a pool stranded by a killed replica is recovered without
 * waiting four hours for the sweep — but it is not a thing to do by brushing
 * past a button, so the count of rows held elsewhere goes in front of the
 * decision rather than into the result.
 *
 * ── One shell, two pools ────────────────────────────────────────────
 * The plumbing is identical — a mutation, a confirm, an invalidate, a result
 * line — and the two differ only in what they say. Written twice, they drifted
 * on the first change: the account tool announced dropped leases and the ISP one
 * would have needed to explain a WAF cooldown instead. The wording is therefore
 * a prop and the mechanism is not.
 */
function ReleaseTool<T>(p: {
  /** Button label — names the thing being freed, not the action. */
  action: string
  blurb: string
  endpoint: string
  dialogTitle: string
  dialogDescription: string
  /** What the write actually does, and what it costs. Shown before the click. */
  dialogBody: React.ReactNode
  renderResult: (result: T) => React.ReactNode
}) {
  const qc = useQueryClient()
  const [asking, setAsking] = React.useState(false)
  const [result, setResult] = React.useState<T | null>(null)

  const release = useMutation({
    mutationFn: () => apiPost<T>(p.endpoint),
    onSuccess: (data) => {
      setResult(data)
      setAsking(false)
      // The server already dropped its cached counts; this refetches them so the
      // line above updates in the same beat rather than up to five seconds later.
      void qc.invalidateQueries({ queryKey: HUNT_SETTINGS_KEY })
    },
  })

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-s2/50 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setAsking(true)}
          disabled={release.isPending}
          className="border-warn/40 text-warn hover:border-warn/60 hover:bg-warn-soft/40"
        >
          {release.isPending ? (
            <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
          ) : (
            <RotateCcw aria-hidden="true" className="size-3.5" />
          )}
          {p.action}
        </Button>
        <span className="text-[12.5px] text-muted">{p.blurb}</span>
      </div>

      {release.isError ? (
        <p role="alert" className="text-[12.5px] text-danger">
          {release.error instanceof Error ? release.error.message : 'The reset failed.'}
        </p>
      ) : null}

      {result !== null ? (
        <p role="status" className="text-[12.5px] text-muted">
          {p.renderResult(result)}
        </p>
      ) : null}

      <Dialog
        open={asking}
        onOpenChange={(open) => {
          if (!release.isPending) setAsking(open)
        }}
        title={p.dialogTitle}
        description={p.dialogDescription}
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setAsking(false)}
              disabled={release.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => release.mutate()}
              disabled={release.isPending}
            >
              {release.isPending ? (
                <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
              ) : null}
              Release everything
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">{p.dialogBody}</div>
      </Dialog>
    </div>
  )
}

/** The warning both pools carry: the collection is not ours alone. */
function SharedPoolWarning({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-warn/30 bg-warn-soft/40 px-3 py-2 text-[12.5px] text-warn">
      <TriangleAlert aria-hidden="true" className="mt-px size-4 shrink-0" />
      <span>{children}</span>
    </div>
  )
}

const Mono = ({ children }: { children: React.ReactNode }) => (
  <span className="font-mono text-[12px]">{children}</span>
)

export function ReleasePoolTool(p: { pool: PoolStats }) {
  return (
    <ReleaseTool<PoolReleaseResult>
      action="Release every account"
      blurb="Recovers a pool stranded by a crash. Not a routine action."
      endpoint="/api/automation/session-accounts/release-all"
      dialogTitle="Release every session account?"
      dialogDescription="Every row in the shared pool goes back to unclaimed, immediately."
      renderResult={(r) => (
        <>
          Freed <strong className="tabular text-fg">{r.freed}</strong> of {r.total} rows
          {r.leasesDropped > 0
            ? ` · dropped ${r.leasesDropped} live lease${
                r.leasesDropped === 1 ? '' : 's'
              } across ${r.huntsAffected} hunt${
                r.huntsAffected === 1 ? '' : 's'
              }, which are refilling now`
            : ''}
          .
        </>
      )}
      dialogBody={
        <>
          <p className="text-muted">
            This sets <Mono>heldBy</Mono>, <Mono>validAfter</Mono> and <Mono>claimedAt</Mono> to
            null on every document in <Mono>session_accounts</Mono>.
          </p>

          {/*
            The one warning worth interrupting for.

            `session_accounts` is shared with the other system, and its holders
            are not ours to clear. Stating it here — before the click, not in
            the result — is the difference between an informed override and a
            surprise.
          */}
          <SharedPoolWarning>
            This pool is shared with the other system. Rows it is holding right now will also be
            freed, so both systems could hand out the same identity until each re-claims. Use it to
            recover a pool stranded by a crash, not routinely.
          </SharedPoolWarning>

          {p.pool.ready && p.pool.held > 0 ? (
            <p className="text-muted">
              This console is currently holding{' '}
              <strong className="tabular text-fg">{p.pool.held}</strong>. Any hunt running now drops
              its borrowed identities and re-claims within a couple of seconds, polling on the
              customer&rsquo;s own session in the gap.
            </p>
          ) : null}
        </>
      }
    />
  )
}

/**
 * The same, for ISP endpoints.
 *
 * ── Why its dialog is not the account one with a word changed ────────
 * Two of the three things it says are different in kind. This write also clears
 * `wafCooldownUntil`, which is the pool's third state and the one that actually
 * empties it — so the benched count belongs in front of the decision, not just
 * in the result. And unlike the account reset, nothing is dropped in memory: a
 * running worker keeps the proxy URL it already has and carries on, because the
 * address still works and it is only the row's claim that has gone.
 */
export function ReleaseIspTool(p: { pool: IspPoolStats }) {
  return (
    <ReleaseTool<IspReleaseResult>
      action="Release every ISP"
      blurb="Recovers a pool stranded by a crash, or benched by a WAF episode."
      endpoint="/api/automation/isp/release-all"
      dialogTitle="Release every ISP endpoint?"
      dialogDescription="Every row goes back to unclaimed and un-benched, immediately."
      renderResult={(r) => (
        <>
          Freed <strong className="tabular text-fg">{r.freed}</strong> of {r.total} rows
          {r.cooling > 0 ? ` · un-benched ${r.cooling} on a WAF cooldown` : ''}
          {r.others > 0 ? ` · ${r.others} were held by the other system` : ''}.
        </>
      )}
      dialogBody={
        <>
          <p className="text-muted">
            This sets <Mono>heldBy</Mono>, <Mono>claimedAt</Mono> and{' '}
            <Mono>wafCooldownUntil</Mono> to null on every document in{' '}
            <Mono>admin_isp_port_assignments</Mono>.
          </p>

          <SharedPoolWarning>
            This pool is shared with the other system. Rows it is holding right now will also be
            freed, so both systems could end up on the same address until each re-claims.
          </SharedPoolWarning>

          {/*
            The half that is specific to this pool, and the reason the button is
            worth having at all on the day it is needed.

            A cooldown is what a WAF challenge writes, and either system may have
            written it. Clearing them all is the fastest way back from an episode
            that benched most of the collection — and it is also the riskiest
            thing here, because every address that was benched for being refused
            goes straight back into rotation. The number is the whole decision.
          */}
          {p.pool.ready && p.pool.cooling > 0 ? (
            <SharedPoolWarning>
              <strong className="tabular">{p.pool.cooling}</strong> of {p.pool.total} rows are
              benched on a WAF cooldown right now. Releasing puts every one of them back into
              rotation immediately, including any that were benched for being refused seconds ago.
            </SharedPoolWarning>
          ) : null}

          <p className="text-muted">
            Hunts already running are not interrupted: each worker keeps the address it has and
            carries on, and hands back nothing when it finishes. The cost is that one address may
            be handed to a second holder before then.
          </p>
        </>
      }
    />
  )
}

/**
 * The ISP pool's budget, in the same shape the account pool's is stated in.
 *
 * ── Why this is a separate ceiling, and not a restatement of the other ──
 * A customer holds ONE address for its session plus one per worker of every
 * fleet, and it holds them for the whole hunt. That is a completely different
 * arithmetic from the account pool — which only the schedule and GA fleets
 * spend, and at `workers × accounts` — so the two ceilings sit at different
 * numbers and either can be the one you actually hit. The page showed only the
 * account one, which is why a fleet could be sized against a budget that was
 * not the binding constraint.
 */
export function IspBudgetLine(p: {
  pool: IspPoolStats
  watchWorkers: number
  gqlWorkers: number
  gaWorkers: number
}) {
  /*
   * One for the customer's own session, then one per worker of each fleet — and
   * the GA fleet is added rather than maxed with the schedule and GraphQL
   * fleets, for the reason `PoolBudgetLine` records: no one customer holds
   * everything at once, but the fleet does, and this figure is a budget for the
   * pool rather than for a row. The GraphQL workers only ever run while a shift
   * has no UUID, so on most Current-flow hunts this is a ceiling, not a cost.
   */
  const perCustomer = 1 + p.watchWorkers + p.gqlWorkers + p.gaWorkers
  const concurrent = p.pool.ready ? Math.floor(p.pool.free / perCustomer) : Infinity

  /*
   * A pool that is mostly benched, said out loud.
   *
   * `free` alone cannot distinguish "everything is in use" from "everything was
   * refused an hour ago and is still sitting out", and the two call for
   * opposite responses. A quarter of the collection is the point at which the
   * cooldowns, not the claims, are what the pool is short of.
   */
  const mostlyCooling = p.pool.ready && p.pool.total > 0 && p.pool.cooling / p.pool.total >= 0.25

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-line bg-s2/50 px-3 py-2 text-[12.5px]">
        <span className="text-muted">
          <strong className="tabular text-fg">{perCustomer}</strong> addresses per customer
          <span className="text-muted/80">
            {' '}
            (1 session + {p.watchWorkers} schedule + {p.gqlWorkers} graphql + {p.gaWorkers} GA)
          </span>
        </span>
        <span aria-hidden="true" className="text-muted/60">
          ·
        </span>
        <span className="text-muted">
          pool has{' '}
          <strong className="tabular text-fg">{p.pool.ready ? p.pool.free : '—'}</strong> free
          {p.pool.ready && p.pool.held > 0 ? `, ${p.pool.held} held by us` : ''}
          {p.pool.ready && p.pool.others > 0 ? `, ${p.pool.others} by the other system` : ''}
          {p.pool.ready && p.pool.cooling > 0 ? (
            <>
              , <span className="text-warn">{p.pool.cooling} cooling</span>
            </>
          ) : (
            ''
          )}
        </span>
        {p.pool.ready ? (
          <span className={cn('tabular ml-auto', concurrent >= 5 ? 'text-muted' : 'text-warn')}>
            ≈ {concurrent} customer{concurrent === 1 ? '' : 's'} at once
          </span>
        ) : null}
      </div>

      {mostlyCooling ? (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-warn/30 bg-warn-soft/40 px-3 py-2 text-[12.5px] text-warn"
        >
          <TriangleAlert aria-hidden="true" className="mt-px size-4 shrink-0" />
          <span>
            <strong className="tabular">{p.pool.cooling}</strong> of {p.pool.total} addresses are
            benched on a WAF cooldown, not in use. That field is shared with the other system, so
            an episode over there benches rows for both. Releasing clears every cooldown at once.
          </span>
        </div>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * What is running right now
 * ------------------------------------------------------------------ */

/**
 * One live hunt, as a single line of counters.
 *
 * This page answers "what is running and is it healthy"; the run drawer answers
 * "what exactly is worker 3 doing". One line each, no expansion, no per-check
 * data crossing the wire — an earlier version rendered a full per-worker table
 * here, fed by a payload that carried every hunt's entire ring whether or not
 * anyone opened it.
 */
const HuntDetail = React.memo(function HuntDetail({ hunt: h }: { hunt: HuntRow }) {
  const stalled = h.rate === 0 && h.elapsedMs > 10_000
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
      <span className="text-[13px] text-fg">{h.customerId}</span>
      <span className="tabular text-[12px] text-muted">{h.scheduleId}</span>
      <span
        className={cn(
          'rounded-xs px-1.5 text-[11px]',
          h.state === 'won'
            ? 'bg-ok-soft text-ok'
            : h.state === 'striking' || h.state === 'bursting'
              ? 'bg-accent/15 text-accent'
              : 'bg-s3 text-muted'
        )}
      >
        {h.state}
      </span>

      <span className="tabular ml-auto flex flex-wrap items-center gap-x-2 text-[12px]">
        {/* Rate first and coloured, because "is it still looping" is the only
            question this list exists to answer at a glance. */}
        <span className={stalled ? 'text-danger' : h.rate > 0 ? 'text-ok' : 'text-muted'}>
          {h.rate > 0 ? `${h.rate}/s` : stalled ? 'stalled' : '—'}
        </span>
        <span className="text-muted">
          {h.workers}w · {h.egress} ISP · {h.accounts} acct
        </span>
        <span className="text-muted">
          {h.polls} checks · {Math.round(h.elapsedMs / 1000)}s
          {h.meanRttMs > 0 ? ` · ${h.meanRttMs}ms` : ''}
        </span>
        {h.errors > 0 ? <span className="text-warn">{h.errors} err</span> : null}
        {h.lastStatus ? <span className="text-muted">{h.lastStatus}</span> : null}
      </span>
    </li>
  )
})

export function HuntNowList({ live }: { live: HuntLive }) {
  return (
    <section className="overflow-hidden rounded-lg border border-line bg-s1">
      <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
        <h2 className="text-[13px] font-medium text-fg">Hunting now</h2>
        <span className="tabular text-[12px] text-muted">{live.tracked} active</span>
      </header>
      {live.hunts.length === 0 ? (
        <p className="px-4 py-8 text-center text-[12.5px] text-muted">
          No hunts running. One starts automatically after a customer&rsquo;s application is
          created.
        </p>
      ) : (
        <ul className="divide-y divide-line/60">
          {live.hunts.map((h) => (
            <HuntDetail key={h.customerId} hunt={h} />
          ))}
        </ul>
      )}
    </section>
  )
}

